import { useCallback, useEffect, useRef, useState } from "react";

import { Button, Container, PortalLayout, PortalPanelBody, RenewalSummaryCard, Stack, Stepper, Typography } from "../../design-system";
import type { StepperItemData } from "../../design-system";
import { useCapability } from "../../capability/use-capability";
import { formatLongDate } from "../../content/format-long-date";
import { formatPolicyLine } from "../../content/policy-line";
import {
  SECTION_TABLE,
  STEP_TABLE,
  STEPPER_STAGE_TABLE,
  toConfirmedAnswers,
  toRenewalData,
} from "../../routing/route-table.ts";
import type { ConfirmedAnswers, RenewalData, StepId } from "../../routing/route-table.ts";
import { resolveResumeTarget } from "../../routing/resume.ts";
import { computeSectionStates, computeStepperStages } from "../../routing/section-state.ts";
import type { ResumeTarget } from "../../routing/types.ts";
import type { WorkingCopyPort } from "../../state/working-copy-port";
import type { RenewalSummaryPort, RenewalSummaryReadResult } from "../../state/renewal-summary-port";
import { getStageLabel, getStepperStatusText, renewalLandingContent as content } from "./content.ts";

/** Keyed by `StepperStageDescriptor.id`, so a `StepperStageEntry` (which
 * carries no labelKey of its own) can be joined back to its declared
 * label -- the same join-by-id-via-Map pattern
 * `MultiSectionStepRoute.tsx` already uses for `SECTION_TABLE`. */
const STAGE_DESCRIPTORS_BY_ID = new Map(STEPPER_STAGE_TABLE.map((stage) => [stage.id, stage]));

/** Every landing state sits in the standard `Container`, the same gutters
 * and max width as every other journey screen (m1-portal-header-footer
 * post-review fix e). */
const LANDING_CONTAINER_TEST_ID = "renewal-landing-container";

/**
 * The default authenticated route (AC-1, AC-2, AC-9). Renders the
 * renewal date, the policies in scope, a status, and exactly one primary
 * call to action — never an exposure capture input (NFR-1783330573181-
 * 1ze2f3). Router-agnostic on purpose: this component names no step
 * path and no route, and hands its resolved navigation target to
 * `onPrimaryAction` — wiring that target to an actual route is
 * TG-4/router.tsx's concern (ADR-20260826-vbpt), not this file's.
 */
export interface RenewalLandingProps {
  readonly renewalId: string;
  readonly renewalSummaryPort: RenewalSummaryPort;
  readonly workingCopyPort: WorkingCopyPort;
  /** OBJ-120/FIND-175: the composition root's own in-session settlement
   * signal (App.tsx's `settledBeyondTargetRef`, the same one
   * router.tsx's guard already honours to admit `/renewal/received`) —
   * fed to `computeStepperStages` so the "Declaration" stepper item can
   * report "done" once the declaration step has actually been settled
   * this session, rather than being hardcoded to never settle. Callers
   * with no real signal must pass `() => false` — never guess `true`. */
  readonly isHostlessStepSettled: (stepId: StepId) => boolean;
  /** Called with resume.ts's resolved target when the customer activates
   * the primary action. Never called with a step or section literal this
   * component invented — the value is always resolveResumeTarget's own
   * return. */
  readonly onPrimaryAction: (target: ResumeTarget) => void;
  /** OBJ-C-18/FIND-851 (revised for OBJ-C-20/FIND-863/867): called once a
   * retry (never the initial mount) resolves, with the SAME outcome this
   * component's own read just produced. This component keeps its own,
   * independent read from the port, entirely separate from the
   * composition root's `captureState` that `RequireRenewalWindowOpen`
   * (App.tsx) gates every route on — without a signal out, a first read
   * that fails here latches App's `captureState` at `"failed"` (re-read
   * only on `reloadToken`/`hasSession` change), and that guard passes
   * every route straight through while `"failed"`, never substituting
   * screen 8, even after this component's own retry succeeds with
   * `isRenewalWindowOpen: false`.
   *
   * OBJ-C-20/FIND-863/867: the original wiring handed the caller only a
   * bare signal, so `App.tsx` could only react by starting ANOTHER full
   * `readSummary`/`readConfirmedSections` round trip of its own
   * (`requestReload`) — one retry activation then issued two real GETs
   * to the same route. Handing the caller this component's OWN just-
   * resolved outcome instead lets it update its gating state directly,
   * with no second read. Callers with no real signal must pass
   * `() => {}` — never omit it where a reload exists. */
  readonly onReadRetried?: (result: LandingRetryResult) => void;
}

type LandingReadState =
  | { phase: "loading" }
  | { phase: "failed" }
  | {
      phase: "ready";
      summary: RenewalSummaryReadResult;
      confirmedAnswers: ConfirmedAnswers;
    };

/** The subset of `LandingReadState` a retry can ever resolve to -- never
 * `"loading"`, since `resolveNextState` always settles to `"failed"` or
 * `"ready"` (its own `try`/`catch` maps every rejection to `"failed"`).
 * Exported so `App.tsx` can apply it to its own `captureState` without
 * this component naming App.tsx's types, or App.tsx re-deriving its own
 * read. */
export type LandingRetryResult = Exclude<LandingReadState, { phase: "loading" }>;

export function RenewalLanding({
  renewalId,
  renewalSummaryPort,
  workingCopyPort,
  isHostlessStepSettled,
  onPrimaryAction,
  onReadRetried,
}: Readonly<RenewalLandingProps>) {
  const capability = useCapability();
  // "loading" is the initial phase only -- a retry (below) re-runs the
  // read without resetting to "loading" first, so the retry control's
  // own previous state (the failure) stays on screen, updated in place,
  // once the new outcome resolves.
  const [state, setState] = useState<LandingReadState>({ phase: "loading" });
  const [attempt, setAttempt] = useState(0);

  // Deliberately does not call setState itself (react-hooks/set-state-in-
  // effect): this resolves the two reads into the NEXT state value only.
  // The effect below is the sole place that applies it, in its own
  // `.then()` callback, guarded against a stale response racing a retry
  // or a renewalId change.
  const resolveNextState = useCallback(async (): Promise<LandingReadState> => {
    // FIND-35/OBJ-20: neither port documents a never-rejects guarantee,
    // and a real HTTP-backed implementation rejects on exactly the
    // trigger AC-9's "failed read" case exists for — a network drop.
    // Without this try/catch a rejection here skips both the Failed
    // check below and the caller's `.then()`, leaving `state.phase`
    // latched at "loading" forever with no stated failure and no retry
    // control, which is precisely the state AC-9 forbids. A rejected
    // read carries no more information than a Failed one at this
    // boundary, so both map to the same closed `{ phase: "failed" }`
    // value the "Failed" branch below already produces.
    try {
      const [summaryOutcome, workingCopyOutcome] = await Promise.all([
        renewalSummaryPort.readSummary(renewalId),
        workingCopyPort.readConfirmedSections(renewalId),
      ]);

      // ADR-20260918-gvy0: a Failed read renders as a stated failure, never
      // as the not-started state — never populate a renewal date or policy
      // list from a partial success (AC-9).
      if (summaryOutcome.status === "Failed" || workingCopyOutcome.status === "Failed") {
        return { phase: "failed" };
      }

      return {
        phase: "ready",
        summary: summaryOutcome.value,
        confirmedAnswers: toConfirmedAnswers(workingCopyOutcome.value.confirmedSections),
      };
    } catch {
      return { phase: "failed" };
    }
  }, [renewalId, renewalSummaryPort, workingCopyPort]);

  // DEBT-20261019-tefp: App.tsx hands a new `onReadRetried` identity on every
  // reload. Read through a ref, so a new identity never re-runs the read
  // effect below: AC-8's "exactly one read" holds by code, not by React
  // batching unmounting this component first.
  const onReadRetriedRef = useRef(onReadRetried);
  useEffect(() => {
    onReadRetriedRef.current = onReadRetried;
  }, [onReadRetried]);

  useEffect(() => {
    let stale = false;
    // OBJ-C-20/FIND-863/867: `attempt > 0` means this run is a retry, not
    // the initial mount -- only a retry's OWN resolved outcome is handed
    // to `onReadRetried`, so the caller applies it instead of starting a
    // second, fully redundant `readSummary`/`readConfirmedSections` round
    // trip of its own. One "Try again" activation now issues exactly one
    // further GET -- this effect's.
    const isRetry = attempt > 0;

    void resolveNextState().then((nextState) => {
      if (!stale) {
        setState(nextState);
        // `resolveNextState` never resolves to `{ phase: "loading" }` --
        // its own `try`/`catch` maps every path to `"failed"` or `"ready"`.
        if (isRetry && nextState.phase !== "loading") {
          onReadRetriedRef.current?.(nextState);
        }
      }
    });

    return () => {
      stale = true;
    };
    // `attempt` is intentionally in the dependency array with no other
    // use: it is the retry control's trigger to re-run the same read.
  }, [resolveNextState, attempt]);

  const retry = useCallback(() => {
    setAttempt((count) => count + 1);
  }, []);

  if (state.phase === "loading") {
    return (
      <PortalLayout headerVariant="entry">
        <Container layout="summary" testId={LANDING_CONTAINER_TEST_ID}>
          <Typography variant="body">{content.loadingMessage}</Typography>
        </Container>
      </PortalLayout>
    );
  }

  if (state.phase === "failed") {
    return (
      <PortalLayout headerVariant="entry">
        <Container layout="summary" testId={LANDING_CONTAINER_TEST_ID}>
          <PortalPanelBody id="renewal-landing-failed-read" expanded tone="error" title={content.failedRead.title} testId="renewal-landing-failed-read">
            <Stack gap="md">
              <Typography variant="body">{content.failedRead.message}</Typography>
              <Button emphasis="primary" onClick={retry} testId="renewal-landing-retry-action">
                {content.failedRead.retryActionLabel}
              </Button>
            </Stack>
          </PortalPanelBody>
        </Container>
      </PortalLayout>
    );
  }

  const { summary, confirmedAnswers } = state;
  const renewalData: RenewalData = toRenewalData(summary.policiesInScope);
  const sectionStates = computeSectionStates(SECTION_TABLE, STEP_TABLE, confirmedAnswers, renewalData);
  // OBJ-130/FIND-190: the same isHostlessStepSettled signal already fed
  // to computeStepperStages below (so the stepper can report Declaration
  // "done") must also reach the primary action's own target resolution,
  // or the two can disagree on the same render -- stepper says
  // Completed, button still lands back on the settled, now-blank step.
  const resumeTarget = resolveResumeTarget(sectionStates, STEP_TABLE, isHostlessStepSettled);
  // "Any progress at all" is a different question from "how many of the 3
  // steps are done" below -- a customer can have confirmed a section (or
  // more than 3, there being more exposure sections than stepper stages)
  // without a full stage having settled yet, and partiallyComplete must
  // still show for that customer.
  const isPartiallyComplete = sectionStates.some((entry) => entry.state === "confirmed");

  const stepperStages = computeStepperStages(STEPPER_STAGE_TABLE, sectionStates, isHostlessStepSettled);
  const stepperItems: StepperItemData[] = stepperStages.map((stage) => ({
    id: stage.stageId,
    label: getStageLabel(STAGE_DESCRIPTORS_BY_ID.get(stage.stageId)?.labelKey ?? stage.stageId),
    status: stage.state,
    statusText: getStepperStatusText(stage.state),
  }));
  // Fix A (ADR pending, fix/m1-portal-design-fixes): content.ts's copy
  // promises "of 3 steps" -- the 3 STEPPER_STAGE_TABLE stages -- so the
  // numerator must be a count of DONE stages, never a count of confirmed
  // SECTION_TABLE entries (there can be more exposure sections than
  // stages, which previously rendered counts like "7 of 3").
  const confirmedCount = stepperStages.filter((stage) => stage.state === "done").length;

  const policyLine = formatPolicyLine(summary.policiesInScope);

  // AC-11: a contributor sees state 2.3 -- never the owner's stepper/
  // resume mechanics above, which this branch renders nothing from.
  // `canSubmitRenewal` is the one capability field that is true for an
  // owner and false for every contributor (capability.ts's own
  // `resolveCapability`), so this is a capability check, not a role
  // check -- the capability boundary (ADR-20260826-c1b3) exposes no role
  // for a screen to branch on directly.
  if (!capability.canSubmitRenewal) {
    const sectionCount = capability.confirmableSectionIds.size;
    return (
      <PortalLayout headerVariant="entry" businessName={summary.businessName}>
        <Container layout="summary" testId={LANDING_CONTAINER_TEST_ID}>
          <RenewalSummaryCard
            businessName={summary.businessName}
            metaRows={[
              // H2 2.3: the colleague sees the renewal date too.
              {
                icon: "calendar",
                content: (
                  <>
                    Renews{" "}
                    <Typography variant="body" as="time" dateTime={summary.renewalDate}>
                      {formatLongDate(summary.renewalDate)}
                    </Typography>
                  </>
                ),
              },
              { icon: "shield", content: `${sectionCount} section${sectionCount === 1 ? "" : "s"} to review` },
            ]}
          >
            <Stack gap="field">
              <Typography variant="bodyLarge">{content.contributor.statusLine(summary.ownerName, sectionCount)}</Typography>
              <Button
                emphasis="primary"
                size="large"
                fullWidth
                onClick={() => onPrimaryAction({ targetStep: "exposures" })}
                testId="renewal-landing-primary-action"
              >
                {content.contributor.primaryActionLabel}
              </Button>
            </Stack>
          </RenewalSummaryCard>
        </Container>
      </PortalLayout>
    );
  }

  const statusContent = isPartiallyComplete ? content.partiallyComplete : content.notStarted;
  const bodyText = isPartiallyComplete
    ? content.partiallyComplete.bodyText(confirmedCount, content.nextStepDescription[resumeTarget.targetStep])
    : content.notStarted.bodyText;

  return (
    <PortalLayout headerVariant="entry" businessName={summary.businessName}>
      <Container layout="summary" testId={LANDING_CONTAINER_TEST_ID}>
        <RenewalSummaryCard
          businessName={summary.businessName}
          metaRows={[
            {
              icon: "calendar",
              content: (
                <>
                  Renews{" "}
                  <Typography variant="body" as="time" dateTime={summary.renewalDate}>
                    {formatLongDate(summary.renewalDate)}
                  </Typography>
                </>
              ),
            },
            { icon: "shield", content: policyLine },
          ]}
        >
          <Stack gap="field">
            {/* H2 (README.md "Screen 2 -- Renewal summary"): "a bodyLarge
                status line; the stepper; one full-width primary button" --
                the status line comes BEFORE the stepper (ADR-20261016-v6nd,
                AC-2's "landing copy order"), reversed from this file's
                earlier, undocumented order. */}
            <Typography variant="bodyLarge">{bodyText}</Typography>
            <Stepper aria-label={content.stepperLabel} items={stepperItems} testId="renewal-landing-stepper" />
            <Button
              emphasis="primary"
              size="large"
              fullWidth
              onClick={() => onPrimaryAction(resumeTarget)}
              testId="renewal-landing-primary-action"
            >
              {statusContent.primaryActionLabel}
            </Button>
          </Stack>
        </RenewalSummaryCard>
      </Container>
    </PortalLayout>
  );
}
